import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { usePlotsList } from '@/hooks/use-plots'
import { getApiErrorMessage } from '@/lib/api'
import { formatKES } from '@/lib/auth'

interface AssignPlotToMemberModalProps {
  open: boolean
  onClose: () => void
  memberName: string
  onAssign: (plotId: string, transferNote?: string, overrideDuesReason?: string) => Promise<void>
  loading?: boolean
}

export function AssignPlotToMemberModal({
  open,
  onClose,
  memberName,
  onAssign,
  loading,
}: AssignPlotToMemberModalProps) {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [plotId, setPlotId] = useState('')
  const [transferNote, setTransferNote] = useState('')
  const [overrideDuesReason, setOverrideDuesReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    if (!open) {
      setSearch('')
      setPlotId('')
      setTransferNote('')
      setOverrideDuesReason('')
      setError(null)
    }
  }, [open])

  const { data } = usePlotsList({
    page: 1,
    limit: 20,
    search: debouncedSearch || undefined,
  })

  const plots = data?.data ?? []
  const selected = plots.find((p) => p.id === plotId)
  const outstandingBalance = selected?.outstandingBalance ?? 0

  if (!open) return null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!plotId) {
      setError('Select a plot')
      return
    }
    if (outstandingBalance > 0 && !overrideDuesReason.trim()) {
      setError('An override reason is required — this plot has an outstanding balance')
      return
    }
    setError(null)
    try {
      await onAssign(
        plotId,
        transferNote.trim() || undefined,
        outstandingBalance > 0 ? overrideDuesReason.trim() : undefined,
      )
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not assign plot'))
    }
  }

  return (
    <div className="fixed inset-0 z-[60]">
      <button
        type="button"
        className="fixed inset-0 bg-navy/40"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div className="fixed inset-0 overflow-y-auto overscroll-contain">
        <div className="flex min-h-full items-end justify-center p-4 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] sm:items-center sm:pb-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="assign-plot-member-title"
            className="relative w-full max-w-md rounded-card border border-border-2 bg-card p-6 shadow-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 text-muted-foreground hover:text-ink"
              aria-label="Close"
            >
              <X className="size-5" />
            </button>

            <h2 id="assign-plot-member-title" className="font-serif text-xl text-ink">
              Assign plot
            </h2>
            <p className="mt-1 font-sans text-sm text-muted-foreground">
              Assign a plot to <strong className="text-ink">{memberName}</strong>
            </p>

            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <Input
                label="Search plots"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Plot number, street, section…"
              />

              <div className="max-h-48 overflow-y-auto rounded border border-border-2">
                {plots.length === 0 ? (
                  <p className="px-3 py-4 font-sans text-sm text-muted-foreground">
                    No plots found
                  </p>
                ) : (
                  plots.map((plot) => (
                    <label
                      key={plot.id}
                      className="flex cursor-pointer items-start gap-3 border-b border-border-2 px-3 py-2 last:border-0 hover:bg-cream-2/50"
                    >
                      <input
                        type="radio"
                        name="plot"
                        checked={plotId === plot.id}
                        onChange={() => setPlotId(plot.id)}
                        className="mt-1"
                      />
                      <span className="min-w-0 font-sans text-sm">
                        <span className="font-medium text-ink">Plot {plot.newNumber}</span>
                        <span className="block text-xs capitalize text-muted-foreground">
                          {plot.status}
                          {plot.currentOwner && plot.currentOwner.name !== memberName && (
                            <> · owned by {plot.currentOwner.name}</>
                          )}
                        </span>
                      </span>
                    </label>
                  ))
                )}
              </div>

              {selected?.currentOwner && selected.currentOwner.name !== memberName && (
                <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 font-sans text-sm text-amber-900">
                  This plot is currently owned by {selected.currentOwner.name}. Assigning will
                  transfer ownership.
                </p>
              )}

              <Input
                label="Transfer note (optional)"
                value={transferNote}
                onChange={(e) => setTransferNote(e.target.value)}
                placeholder="Reason for assignment or transfer"
              />

              {outstandingBalance > 0 && (
                <div className="space-y-3 rounded border border-destructive/30 bg-destructive/5 px-3 py-2.5">
                  <p className="font-sans text-sm text-destructive">
                    This plot has an outstanding balance of{' '}
                    <strong>{formatKES(outstandingBalance)}</strong>. Transferring it requires an
                    override reason.
                  </p>
                  <Input
                    label="Override reason"
                    value={overrideDuesReason}
                    onChange={(e) => setOverrideDuesReason(e.target.value)}
                    placeholder="e.g. foreclosure, correction, hardship waiver"
                    required
                  />
                </div>
              )}

              {error && (
                <p className="font-sans text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" loading={loading}>
                  Assign
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
